import React, { useEffect, useState } from 'react'
import "./Navbar.css"
import logo from './valhalla-black.webp'
import { Navbar, Nav } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import { FiMenu, FiX } from 'react-icons/fi';
import { useLocation, useNavigate } from "react-router-dom"
import { clearAuthSession, getCurrentUser, isAuthenticated } from '../utils/auth';

function NavbarTop() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = getCurrentUser();
    const userScopes = user?.scopes || [];
    const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 860);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const apiBasePath = String(process.env.REACT_APP_API_URL_BASE ?? '').trim();
    const currentUserTypeId = Number(user?.user_type_id ?? user?.user_type);
    const userTypeName = String(user?.user_type_description || user?.description || '').trim();
    const normalizedUserTypeName = userTypeName.toLowerCase();

    const rolePillLabel = (() => {
        if (currentUserTypeId === 10) {
            return 'Creator';
        }

        if (currentUserTypeId === 9) {
            return 'Corp Admin';
        }

        if (currentUserTypeId === 2) {
            return 'Admin';
        }

        if (currentUserTypeId === 1) {
            return null;
        }

        // Fallback for payloads without stable user type IDs.
        if (normalizedUserTypeName.includes('creator')) {
            return 'Creator';
        }

        if (normalizedUserTypeName.includes('admin')) {
            return 'Admin';
        }

        return null;
    })();

    const rolePillTooltip = rolePillLabel === 'Creator'
        ? (apiBasePath || 'API base path not configured')
        : '';

    const renderNavItem = (path, label, className = 'navbar-item') => (
        <Nav.Item onClick={() => navigate(path)}>
            <span className={className}>{label}</span>
        </Nav.Item>
    );

    const navigateAndCloseMenu = (path) => {
        setIsMobileMenuOpen(false);
        navigate(path);
    };

    const handleSignOut = () => {
        setIsMobileMenuOpen(false);
        clearAuthSession();
        navigate('/login');
    };

    const canReadAny = (scopeList) => scopeList.some((scope) =>
        userScopes.some((userScope) => userScope === scope || userScope.startsWith(scope))
    );

    const readScopedLinks = [
        {
            path: '/clients',
            label: 'Clients',
            scopes: ['read:clientData', 'read:clientDataCustomer'],
        },
        {
            path: '/Customers',
            label: 'Customers',
            scopes: ['read:customerData', 'read:customerDataCustomer'],
        },
        {
            path: '/Facilities',
            label: 'Facilities',
            scopes: ['read:facilityData', 'read:facilityDataCustomer'],
        },
        {
            path: '/pods',
            label: 'Pods',
            scopes: ['read:podData', 'read:podDataCustomer'],
        },
        {
            path: '/users',
            label: 'Users',
            scopes: ['read:userData', 'read:userDataCustomer'],
        },
        {
            path: '/protocols',
            label: 'Protocols',
            scopes: ['read:protocolData', 'read:protocolDataCustomer'],
        },
        {
            path: '/protocolusage',
            label: 'Subscriptions',
            scopes: ['read:subscriptionData', 'read:subscriptionDataCustomer'],
        },
    ];

    const visibleReadLinks = isAuthenticated()
        ? readScopedLinks.filter((link) => canReadAny(link.scopes))
        : [];

    const primaryLinks = [
        { path: '/', label: 'Home' },
        ...visibleReadLinks,
        { path: '/protocolusage', label: 'Support' },
    ];

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            const nextIsMobileView = window.innerWidth <= 860;
            setIsMobileView(nextIsMobileView);

            if (!nextIsMobileView) {
                setIsMobileMenuOpen(false);
            }
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const loggedInNavParts = () => {
        if (isAuthenticated() && user) {
            const accountName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || 'Account';
            const accountTitle = (
                <span className='navbar-account-title'>
                    {rolePillLabel && (
                        <span
                            className={`navbar-role-pill ${rolePillLabel === 'Creator' ? 'creator' : 'admin'}`}
                            title={rolePillTooltip}
                        >
                            {rolePillLabel}
                        </span>
                    )}
                    <span className='navbar-account-name'>{accountName}</span>
                </span>
            );

            return (
                <Nav pullRight>
                    <Nav.Menu title={accountTitle} className="navbar-item">
                        {/* <Nav.Item onClick={() => navigate('/userprofile')}><span className="navbar-menu">Settings</span></Nav.Item> */}
                        <Nav.Item onClick={() => navigate('/resetpassword')}><span className="navbar-menu">Reset Password</span></Nav.Item>
                        <Nav.Item onClick={() => handleSignOut()}><span className="navbar-menu">Sign Out</span></Nav.Item>
                    </Nav.Menu>
                </Nav>
            );
        }

        return (
            <Nav pullRight>
                {renderNavItem('/login', 'Sign In')}
            </Nav>
        );
    };

    return (
        <>
            <Navbar className='top-navbar'>
                {isMobileView ? (
                    <div className='top-navbar-mobile-header'>
                        <button
                            type='button'
                            className='navbar-logo-button'
                            onClick={() => navigateAndCloseMenu('/')}
                            aria-label='Go to home page'
                        >
                            <span className='navbar-logo'>
                                <img src={logo} width={115} alt='Valhalla Logo'></img>
                            </span>
                        </button>

                        <button
                            type='button'
                            className='navbar-mobile-toggle'
                            onClick={() => setIsMobileMenuOpen((currentValue) => !currentValue)}
                            aria-expanded={isMobileMenuOpen}
                            aria-controls='mobile-navigation-panel'
                            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                        >
                            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
                        </button>
                    </div>
                ) : (
                    <>
                        <Nav className='top-navbar-main'>
                            <Nav.Item onClick={() => navigate('/')}>
                                <span className='navbar-logo'>
                                    <img src={logo} width={115} alt='Valhalla Logo'></img>
                                </span>
                            </Nav.Item>
                            {primaryLinks.map((link) => (
                                <Nav.Item key={`${link.path}-${link.label}`} onClick={() => navigate(link.path)}>
                                    <span className='navbar-item'>{link.label}</span>
                                </Nav.Item>
                            ))}
                        </Nav>
                        <div className='top-navbar-account'>
                            {loggedInNavParts()}
                        </div>
                    </>
                )}
            </Navbar>

            {isMobileView && (
                <>
                    <div className={`navbar-mobile-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={() => setIsMobileMenuOpen(false)} />
                    <aside
                        id='mobile-navigation-panel'
                        className={`navbar-mobile-panel ${isMobileMenuOpen ? 'open' : ''}`}
                        aria-hidden={!isMobileMenuOpen}
                    >
                        <div className='navbar-mobile-panel-header'>
                            <span className='navbar-mobile-panel-title'>Navigation</span>
                            <button
                                type='button'
                                className='navbar-mobile-close'
                                onClick={() => setIsMobileMenuOpen(false)}
                                aria-label='Close navigation menu'
                            >
                                <FiX />
                            </button>
                        </div>

                        <div className='navbar-mobile-links'>
                            {primaryLinks.map((link) => (
                                <button
                                    key={`mobile-${link.path}-${link.label}`}
                                    type='button'
                                    className='navbar-mobile-link'
                                    onClick={() => navigateAndCloseMenu(link.path)}
                                >
                                    {link.label}
                                </button>
                            ))}
                        </div>

                        <div className='navbar-mobile-account-actions'>
                            {isAuthenticated() && user ? (
                                <>
                                    <div className='navbar-mobile-account-name'>{`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || 'Account'}</div>
                                    <button type='button' className='navbar-mobile-link secondary' onClick={() => navigateAndCloseMenu('/resetpassword')}>
                                        Reset Password
                                    </button>
                                    <button type='button' className='navbar-mobile-link danger' onClick={handleSignOut}>
                                        Sign Out
                                    </button>
                                </>
                            ) : (
                                <button type='button' className='navbar-mobile-link' onClick={() => navigateAndCloseMenu('/login')}>
                                    Sign In
                                </button>
                            )}
                        </div>
                    </aside>
                </>
            )}
        </>
    )
}

export default NavbarTop



